import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

/**
 * Service for managing Web Push notifications (mobile support).
 * This is an ADDITIVE feature - does NOT affect existing WebSocket notifications.
 */
@Injectable({
    providedIn: 'root'
})
export class PushNotificationService {

    private readonly API_URL = environment.apiUrl + '/push';
    private swRegistration: ServiceWorkerRegistration | null = null;

    constructor(private http: HttpClient) { }

    /**
     * Initialize push notifications (register service worker and subscribe).
     * Call this after user login.
     */
    async initializePushNotifications(): Promise<boolean> {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            console.warn('[PUSH] Push notifications not supported in this browser');
            return false;
        }

        try {
            // Register service worker
            this.swRegistration = await navigator.serviceWorker.register('/sw.js', {
                scope: '/'
            });
            console.log('[PUSH] ✅ Service Worker registered');

            // 1. Get current VAPID public key from backend
            const response = await this.http.get<{ publicKey: string }>(
                `${this.API_URL}/vapid-public-key`
            ).toPromise();

            if (!response || !response.publicKey) {
                console.error('[PUSH] No VAPID public key received from server');
                return false;
            }
            const serverKey = response.publicKey;

            // 2. Check current subscription
            const existingSubscription = await this.swRegistration.pushManager.getSubscription();

            if (existingSubscription) {
                // Check if key changed (VAPID rotation)
                const currentKeyArray = existingSubscription.options.applicationServerKey;
                let keysMatch = false;

                if (currentKeyArray) {
                    const currentKeyBase64 = this.arrayBufferToUrlBase64(currentKeyArray);
                    // Compare keys (ignoring potential padding differences if not normalized)
                    keysMatch = currentKeyBase64 === serverKey;
                }

                if (keysMatch) {
                    console.log('[PUSH] Already subscribed with valid key');
                    return true;
                } else {
                    console.warn('[PUSH] VAPID key changed (or invalid). Rotating subscription...');
                    await existingSubscription.unsubscribe();
                    // Proceed to re-subscribe
                }
            }

            // 3. Request permission
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                console.warn('[PUSH] Notification permission denied');
                return false;
            }

            // 4. Subscribe to push with new key
            return await this.subscribeToPush(serverKey);
        } catch (error) {
            console.error('[PUSH] Failed to initialize:', error);
            return false;
        }
    }

    /**
     * Subscribe to push notifications.
     */
    private async subscribeToPush(serverPublicKey: string): Promise<boolean> {
        try {
            if (!this.swRegistration) {
                throw new Error('Service Worker not registered');
            }

            // Subscribe
            const subscription = await this.swRegistration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: this.urlBase64ToUint8Array(serverPublicKey)
            });

            // Send subscription to backend
            const subscriptionJSON = subscription.toJSON();
            await this.http.post(`${this.API_URL}/subscribe`, subscriptionJSON).toPromise();

            console.log('[PUSH] ✅ Successfully subscribed to push notifications');
            return true;
        } catch (error) {
            console.error('[PUSH] Failed to subscribe:', error);
            return false;
        }
    }

    /**
     * Unsubscribe from push notifications.
     */
    async unsubscribe(): Promise<boolean> {
        try {
            if (!this.swRegistration) {
                return false;
            }

            const subscription = await this.swRegistration.pushManager.getSubscription();
            if (!subscription) {
                return true;
            }

            // Unsubscribe from browser
            await subscription.unsubscribe();

            // Notify backend
            const subscriptionJSON = subscription.toJSON();
            await this.http.delete(`${this.API_URL}/unsubscribe`, {
                body: { endpoint: subscriptionJSON.endpoint }
            }).toPromise();

            console.log('[PUSH] ✅ Unsubscribed from push notifications');
            return true;
        } catch (error) {
            console.error('[PUSH] Failed to unsubscribe:', error);
            return false;
        }
    }

    /**
     * Convert VAPID public key from base64 to Uint8Array.
     */
    private urlBase64ToUint8Array(base64String: string): Uint8Array {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    private arrayBufferToUrlBase64(buffer: ArrayBuffer): string {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');
    }

    /**
     * Send a test notification request to the backend.
     */
    async sendTestNotification(): Promise<boolean> {
        try {
            await this.http.post(`${this.API_URL}/test`, {}).toPromise();
            return true;
        } catch (error) {
            console.error('[PUSH] Failed to send test notification:', error);
            return false;
        }
    }
    /**
     * Get the current notification permission status.
     */
    getPermissionStatus(): NotificationPermission {
        if (!('Notification' in window)) {
            return 'denied';
        }
        return Notification.permission;
    }
}
